import { Fragment, type ReactElement } from 'react';
import { useTokens } from '@bravemove/design-tokens/react';
import {
  AlertStatusIcon,
  CheckStatusIcon,
  CrossStatusIcon,
  InfoStatusIcon,
} from '@bravemove/design-tokens/icons';

/**
 * Lifecycle state union — mirrors `BookingStatus` from `@bravemove/types/booking`
 * verbatim. Kept local to avoid pulling `@bravemove/types` into `@bravemove/ui-web`'s
 * dependency graph, matching the established pattern in `BookingStatusRibbon`.
 */
export type LifecycleState = 'pending' | 'confirmed' | 'completed' | 'cancelled' | 'no_show';

export interface StateRibbonProps {
  /** The currently active lifecycle state. */
  readonly current: LifecycleState;
  /**
   * Compact mode shrinks the main backbone nodes from 22 px to 18 px and
   * hides the under-label captions. The alternate forked-endpoint row is
   * also suppressed in compact mode (per the design spec).
   */
  readonly compact?: boolean;
  readonly className?: string;
}

interface StateCfg {
  readonly fg: string;
  readonly soft: string;
  readonly br: string;
  readonly label: string;
}

const MAIN_PATH: readonly LifecycleState[] = ['pending', 'confirmed', 'completed'];
const FORKED_PATH: readonly LifecycleState[] = ['cancelled', 'no_show'];

function getStateCfg(state: LifecycleState, palette: ReturnType<typeof useTokens>): StateCfg {
  switch (state) {
    case 'pending':
      return {
        fg: palette.color.warning,
        soft: palette.color.warning + '26', // ~15% alpha
        br: palette.color.warning + '66',
        label: 'PENDING',
      };
    case 'confirmed':
      return {
        fg: palette.color.info,
        soft: palette.color.info + '26',
        br: palette.color.info + '66',
        label: 'CONFIRMED',
      };
    case 'completed':
      return {
        fg: palette.color.success,
        soft: palette.color.success + '26',
        br: palette.color.success + '66',
        label: 'COMPLETED',
      };
    case 'cancelled':
      return {
        fg: palette.color.danger,
        soft: palette.color.danger + '26',
        br: palette.color.danger + '66',
        label: 'CANCELLED',
      };
    case 'no_show':
      // Distinct from `cancelled` via the glyph + label; tone stays in the
      // danger family per the founder's locked spec direction.
      return {
        fg: palette.color.danger,
        soft: palette.color.danger + '26',
        br: palette.color.danger + '66',
        label: 'NO-SHOW',
      };
  }
}

function renderGlyph(state: LifecycleState, color: string, size: number): ReactElement {
  switch (state) {
    case 'pending':
      return <InfoStatusIcon color={color} size={size} />;
    case 'confirmed':
    case 'completed':
      return <CheckStatusIcon color={color} size={size} />;
    case 'cancelled':
      return <CrossStatusIcon color={color} size={size} />;
    case 'no_show':
      return <AlertStatusIcon color={color} size={size} />;
  }
}

/**
 * `StateRibbon` — multi-node horizontal lifecycle backbone for the booking
 * detail hero. Three main nodes connected by a progress bar render the
 * happy path (pending → confirmed → completed); two dimmed forked-endpoint
 * pills (cancelled / no_show) render below and snap to full color when the
 * current state matches.
 *
 * Distinct from `BookingStatusRibbon` — that component is the in-row
 * 5-state semantic pill (used inside `BookingCard`, calendar tooltips,
 * admin Tables). `StateRibbon` is the rich hero treatment for the booking
 * detail page on every surface.
 */
export function StateRibbon({
  current,
  compact = false,
  className,
}: StateRibbonProps): ReactElement {
  const t = useTokens();

  const idx = MAIN_PATH.indexOf(current);
  const isAlt = current === 'cancelled' || current === 'no_show';
  // When an alternate endpoint is current, the backbone visually anchors at
  // `confirmed` (the last common ancestor of the fork). Progress halts there.
  const upTo = isAlt ? 1 : Math.max(0, idx);

  const nodeSize = compact ? 18 : 22;
  const glyphSize = compact ? 10 : 12;
  const confirmedCfg = getStateCfg('confirmed', t);

  return (
    <div
      className={className}
      data-testid="state-ribbon"
      data-current={current}
      style={{ display: 'flex', flexDirection: 'column', gap: compact ? 8 : 12 }}
    >
      {/* Main backbone */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 0,
          position: 'relative',
        }}
      >
        {MAIN_PATH.map((s, i) => {
          const reached = !isAlt && i <= upTo;
          const isCur = !isAlt && s === current;
          const c = getStateCfg(s, t);
          const filled = reached || isCur;
          // Progress-bar width to the right of this node:
          //   - 100% if the next node has been reached (i < upTo)
          //   - 50% if this node IS the current main-path node (i === upTo)
          //   - 0% if we haven't reached this node yet
          const segmentWidth = i < upTo ? '100%' : i === upTo && !isAlt ? '50%' : '0%';
          return (
            <Fragment key={s}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 6,
                  flexShrink: 0,
                }}
              >
                <div
                  data-testid={`state-ribbon-node-${s}`}
                  data-reached={filled ? 'true' : 'false'}
                  data-current={isCur ? 'true' : 'false'}
                  style={{
                    width: nodeSize,
                    height: nodeSize,
                    borderRadius: 999,
                    background: filled ? c.fg : t.color.surface2,
                    border: `2px solid ${filled ? c.fg : t.color.hairline2}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: isCur ? `0 0 0 4px ${c.fg}40` : 'none',
                    transition: 'all 200ms',
                  }}
                >
                  {filled ? (
                    renderGlyph(s, t.color.accentInk, glyphSize)
                  ) : (
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: 999,
                        background: t.color.ink3,
                      }}
                    />
                  )}
                </div>
                {!compact && (
                  <div style={{ textAlign: 'center', minWidth: 64 }}>
                    <div
                      style={{
                        fontFamily: t.type.mono.family,
                        fontSize: 9.5,
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                        color: filled ? c.fg : t.color.ink3,
                      }}
                    >
                      {c.label}
                    </div>
                  </div>
                )}
              </div>
              {i < MAIN_PATH.length - 1 && (
                <div
                  data-testid={`state-ribbon-bar-${i}`}
                  data-fill={segmentWidth}
                  style={{
                    flex: 1,
                    height: 3,
                    borderRadius: 999,
                    background: t.color.surface2,
                    position: 'relative',
                    marginInline: 4,
                    marginBlockStart: compact ? 0 : -22,
                    transition: 'opacity 200ms',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      insetBlockStart: 0,
                      insetBlockEnd: 0,
                      insetInlineStart: 0,
                      width: segmentWidth,
                      background: confirmedCfg.fg,
                      borderRadius: 999,
                      transition: 'width 320ms',
                    }}
                  />
                </div>
              )}
            </Fragment>
          );
        })}
      </div>

      {/* Alternate endpoints — render dimmed unless current matches. */}
      {!compact && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          {FORKED_PATH.map((s) => {
            const c = getStateCfg(s, t);
            const isCur = s === current;
            return (
              <div
                key={s}
                data-testid={`state-ribbon-fork-${s}`}
                data-current={isCur ? 'true' : 'false'}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 7,
                  padding: '5px 10px',
                  borderRadius: 999,
                  background: isCur ? c.soft : 'transparent',
                  border: `1px solid ${isCur ? c.br : t.color.hairline}`,
                  opacity: isCur ? 1 : 0.5,
                  transition: 'opacity 200ms',
                }}
              >
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: 999,
                    background: c.fg,
                  }}
                />
                <span
                  style={{
                    fontFamily: t.type.mono.family,
                    fontSize: 9.5,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    color: isCur ? c.fg : t.color.ink3,
                  }}
                >
                  {c.label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default StateRibbon;
